import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";

const AnimatedCircularProgress = ({
  score = 0,
  maxScore = 100,
  size = 250,
  primaryColor: propPrimaryStr,
  secondaryColor: propSecondaryStr,
}) => {
  const [currentScore, setCurrentScore] = useState(0);

  let activePrimary = "#ef4444";
  let activeSecondary = "#b91c1c";
  let activeLabel = "Needs Improvement";

  if (score < 50) {
    activePrimary = "#ef4444";
    activeSecondary = "#b91c1c";
    activeLabel = "Needs Improvement";
  } else if (score <= 75) {
    activePrimary = "#f59e0b";
    activeSecondary = "#b45309";
    activeLabel = "Keep Improving";
  } else {
    activePrimary = "#10b981";
    activeSecondary = "#059669";
    activeLabel = "Excellent";
  }

  const primaryColor = propPrimaryStr || activePrimary;
  const secondaryColor = propSecondaryStr || activeSecondary;

  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const interval = duration / steps;
    const increment = score / steps;
    let current = 0;

    if (score === 0) {
      setCurrentScore(0);
      return;
    }

    const timer = setInterval(() => {
      current += increment;
      if (current >= score) {
        current = score;
        clearInterval(timer);
      }
      setCurrentScore(Math.round(current));
    }, interval);

    return () => clearInterval(timer);
  }, [score]);

  const waveAnimation = {
    animate: {
      x: ["0%", "-50%"],
      transition: {
        repeat: Infinity,
        duration: 8,
        ease: "linear",
      },
    },
  };
  const backWaveAnimation = {
    animate: {
      x: ["-50%", "0%"],
      transition: {
        repeat: Infinity,
        duration: 12,
        ease: "linear",
      },
    },
  };

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        maxWidth: size,
        height: "auto",
        aspectRatio: "1 / 1",
        margin: "0 auto",
        borderRadius: "50%",
        background: "linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)",
        boxShadow:
          "inset 0 2px 10px rgba(0,0,0,0.1), 0 10px 20px rgba(0,0,0,0.1)",
        overflow: "hidden",
        flexShrink: 0,
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: "50%",
          border: `6px solid ${primaryColor}20`,
          zIndex: 2,
        }}
      />

      <motion.div
        initial={{ height: "0%" }}
        animate={{ height: `${score}%` }}
        transition={{ duration: 1.5, ease: "easeInOut" }}
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%",
          zIndex: 1,
          background: primaryColor,
        }}
      >
        <div
          style={{
            position: "absolute",
            bottom: "100%",
            left: 0,
            width: "100%",
            height: size / 5,
            overflow: "hidden",
            marginBottom: "-1px",
          }}
        >
          <motion.div
            variants={backWaveAnimation}
            animate="animate"
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "180%",
              height: "100%",
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 1440 320' preserveAspectRatio='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill='${encodeURIComponent(
                secondaryColor
              )}' fill-opacity='0.6' d='M0,192L48,197.3C96,203,192,213,288,229.3C384,245,480,267,576,250.7C672,235,768,181,864,181.3C960,181,1056,235,1152,234.7C1248,235,1344,181,1392,154.7L1440,128L1440,320L0,320Z'%3E%3C/path%3E%3C/svg%3E")`,
              backgroundRepeat: "repeat-x",
              backgroundPosition: "bottom",
              backgroundSize: "50% 100%",
            }}
          />

          <motion.div
            variants={waveAnimation}
            animate="animate"
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "180%",
              height: "100%",
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 1440 320' preserveAspectRatio='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill='${encodeURIComponent(
                primaryColor
              )}' fill-opacity='1' d='M0,160L48,176C96,192,192,224,288,224C384,224,480,192,576,165.3C672,139,768,117,864,128C960,139,1056,181,1152,197.3C1248,213,1344,203,1392,197.3L1440,192L1440,320L0,320Z'%3E%3C/path%3E%3C/svg%3E")`,
              backgroundRepeat: "repeat-x",
              backgroundPosition: "bottom",
              backgroundSize: "50% 100%",
            }}
          />
        </div>
      </motion.div>

      <div
        style={{
          position: "absolute",
          top: "5%",
          left: "10%",
          right: "10%",
          height: "35%",
          background:
            "linear-gradient(to bottom, rgba(255,255,255,0.7) 0%, rgba(255,255,255,0.1) 100%)",
          borderRadius: "50%",
          pointerEvents: "none",
          zIndex: 10,
          filter: "blur(2px)",
        }}
      />

      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 20,
        }}
      >
        <div
          style={{
            fontSize: "16px",
            color: currentScore > 55 ? "#fff" : "#6B7280",
            fontWeight: 600,
            marginBottom: 4,
            transition: "color 0.3s",
            textShadow:
              currentScore > 55 ? "0 1px 2px rgba(0,0,0,0.2)" : "none",
          }}
        >
          {activeLabel}
        </div>
        <div
          style={{
            fontSize: "56px",
            fontWeight: 800,
            color: currentScore > 50 ? "#fff" : "000",
            lineHeight: 1,
            transition: "color 0.3s",
            textShadow:
              currentScore > 50 ? "0 2px 4px rgba(0,0,0,0.3)" : "none",
          }}
        >
          {currentScore}%
        </div>
      </div>
    </div>
  );
};

export default AnimatedCircularProgress;
